import {
  CMSDataList,
  Policy,
  PublicCredentialRecord,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  Portal,
  Spinner,
  FieldRoot,
  FieldLabel,
  FieldErrorText,
} from "@chakra-ui/react";
import {
  FormEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { isEmail } from "validator";
import { apiReq } from "../../lib/apiReq";
import { EmailErrors } from "./AuthenticationDialogTypes";
import { store } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { cmsRequest } from "../../lib/cmsHelpers";
import { StandardMarkdown } from "../StandardMarkdown/StandardMarkdown";

interface EmailScreenProps {
  confirmLabel: string;
  onNext: (email: string) => void;
  onClose?: () => void;
  defaultEmail?: string;
  /**
   * Show `defaultEmail` read-only. For a page that already knows which
   * address must sign in, such as a partner invitation.
   */
  lockEmail?: boolean;
}

export const EmailScreen = ({
  confirmLabel,
  onNext,
  onClose,
  defaultEmail,
  lockEmail = false,
}: EmailScreenProps) => {
  const registerForm = useRef<HTMLFormElement | null>(null);
  const [registerErrors, setRegisterErrors] = useState<EmailErrors>({});
  const [visiblePolicy, setVisiblePolicy] = useState<string | null>(null);
  const [registerRequest, registerRequestState] =
    useReq<PublicCredentialRecord>(apiReq);
  const [policyReq, policyReqState] = useReq<CMSDataList<Policy>>(cmsRequest);

  useEffect(() => {
    if (visiblePolicy) {
      policyReq.get(`/api/policies?filters[slug][$eq]=${visiblePolicy}`);
    }
  }, [visiblePolicy, policyReq]);

  const register = useCallback<FormEventHandler>(
    async (e) => {
      e.preventDefault();
      const data = new FormData(registerForm.current!);
      const errors: EmailErrors = {};
      const emailFromForm = data.get("email")?.toString();
      if (!emailFromForm) {
        errors.email = "Please enter your email";
      } else if (!isEmail(emailFromForm)) {
        errors.email = "Please double check your email";
      }

      setRegisterErrors(errors);
      if (!Object.keys(errors).length) {
        registerRequestState.clearError();
        const response = await registerRequest.post("/user", {
          email: emailFromForm,
        });
        if (response.success) {
          store.dispatch({
            type: ActionType.UpdateAuth,
            params: {
              emailCredentialId: response.result.id,
            },
          });
          onNext(emailFromForm!);
        }
      }
    },
    [onNext, registerRequest, registerRequestState]
  );

  return (
    <Flex
      as="form"
      ref={registerForm as unknown as React.RefObject<HTMLDivElement>}
      flex={1}
      px="0.25rem"
      direction="column"
      justify="space-between"
      onSubmit={register}
    >
      <Box flex={1}>
        {registerRequestState.error && (
          <Box
            mb="1rem"
            p="3"
            bg="red.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="red.500"
          >
            <Text color="red.700" fontWeight="700">
              There was a problem logging in
            </Text>
          </Box>
        )}
        <FieldRoot invalid={!!registerErrors.email}>
          <FieldLabel
            as="h2"
            fontSize="xl"
            fontWeight="700"
            mb="0.5rem"
            textAlign="center"
            justifyContent="center"
            w="100%"
          >
            Email
          </FieldLabel>
          <Input
            name="email"
            type="email"
            // The visible "Email" label is a FieldLabel rendered as="h2", which
            // drops the htmlFor/id association — so give the input its own
            // accessible name for assistive tech (and getByLabel in tests).
            aria-label="Email"
            autoCapitalize="none"
            autoComplete="email"
            defaultValue={defaultEmail}
            readOnly={lockEmail}
            bg={lockEmail ? "bg.subtle" : undefined}
            size="lg"
            fontSize="md"
            height="3.25rem"
          />
          {registerErrors.email && (
            <FieldErrorText>{registerErrors.email}</FieldErrorText>
          )}
        </FieldRoot>
      </Box>
      <Text fontSize="sm" mt="1rem">
        By using Green Flag you agree to our{" "}
        <Text
          as="span"
          textDecoration="underline"
          cursor="pointer"
          onClick={() => setVisiblePolicy("terms")}
        >
          Terms of Service
        </Text>
        ,{" "}
        <Text
          as="span"
          textDecoration="underline"
          cursor="pointer"
          onClick={() => setVisiblePolicy("privacy")}
        >
          Privacy Policy
        </Text>
        , and{" "}
        <Text
          as="span"
          textDecoration="underline"
          cursor="pointer"
          onClick={() => setVisiblePolicy("waiver")}
        >
          Release of Liability
        </Text>
        .
      </Text>
      <Flex
        py="1rem"
        direction={{ base: "column-reverse", sm: "row" }}
        gap="0.65rem"
      >
        {!!onClose && (
          <Button
            variant="ghost"
            flex={1}
            onClick={onClose}
            type="button"
            disabled={registerRequestState.loading}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="solid"
          colorPalette="greenDark"
          flex={2}
          type="submit"
          size="lg"
          minH="44px"
          disabled={registerRequestState.loading}
        >
          {confirmLabel}
        </Button>
      </Flex>

      <DialogRoot
        open={!!visiblePolicy}
        onOpenChange={(details) => {
          if (!details.open) {
            setVisiblePolicy(null);
          }
        }}
        size="lg"
        scrollBehavior="inside"
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {policyReqState.result?.data.at(0)?.attributes.title || ""}
                </DialogTitle>
              </DialogHeader>
              <DialogBody>
                {policyReqState.loading ? (
                  <Flex justify="center" align="center" minH="20rem">
                    <Spinner size="lg" />
                  </Flex>
                ) : (
                  !!policyReqState.result?.data.at(0)?.attributes?.content && (
                    <Box pb="1rem">
                      <StandardMarkdown
                        content={
                          policyReqState.result.data.at(0)!.attributes.content
                        }
                      />
                    </Box>
                  )
                )}
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="solid"
                  colorPalette="greenDark"
                  onClick={() => setVisiblePolicy(null)}
                >
                  Close
                </Button>
              </DialogFooter>
              <DialogCloseTrigger />
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    </Flex>
  );
};
